<template>
  <section class="d-grid gap-3">
    <header class="d-flex justify-content-between align-items-start flex-wrap gap-3">
      <div>
        <h2 class="h5 mb-1">Ingresos por período</h2>
        <p class="text-muted small mb-0">
          Toda la plata que entró a caja: cobros de suscripción, ventas de productos e
          ingresos varios, en la misma tabla.
        </p>
      </div>
      <button class="btn btn-outline-dark btn-sm" :disabled="loading || !filas.length" @click="exportar">
        <i class="bi bi-download me-1"></i>Exportar CSV
      </button>
    </header>

    <div class="card shadow-sm">
      <div class="card-body">
        <div class="row g-2 align-items-end">
          <div class="col-auto">
            <label class="form-label small mb-1" for="ing-desde">Desde</label>
            <input id="ing-desde" v-model="filtros.fecha_desde" type="date" class="form-control form-control-sm" />
          </div>
          <div class="col-auto">
            <label class="form-label small mb-1" for="ing-hasta">Hasta</label>
            <input id="ing-hasta" v-model="filtros.fecha_hasta" type="date" class="form-control form-control-sm" />
          </div>
          <div class="col-auto">
            <label class="form-label small mb-1" for="ing-tipo">Tipo</label>
            <select id="ing-tipo" v-model="filtros.tipo" class="form-select form-select-sm"
                    style="width: 210px" @change="cargar">
              <option value="">Todos</option>
              <option v-for="t in TIPOS" :key="t.valor" :value="t.valor">{{ t.texto }}</option>
            </select>
          </div>
          <div class="col-auto">
            <label class="form-label small mb-1" for="ing-caja">Caja</label>
            <input id="ing-caja" v-model="filtros.caja_id" type="number" min="1"
                   class="form-control form-control-sm" placeholder="Todas" style="width: 110px" />
          </div>
          <div class="col-auto">
            <button class="btn btn-dark btn-sm" :disabled="loading" @click="cargar">
              {{ loading ? 'Buscando...' : 'Buscar' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger mb-0">{{ error }}</div>

    <div v-if="totales" class="row g-3">
      <div class="col-md-3">
        <div class="card shadow-sm"><div class="card-body">
          <div class="text-muted small">Total ingresado</div>
          <div class="h5 mb-0">{{ formatoMonto(totales.monto_total) }}</div>
        </div></div>
      </div>
      <div class="col-md-3">
        <div class="card shadow-sm"><div class="card-body">
          <div class="text-muted small">Movimientos</div>
          <div class="h5 mb-0">{{ totales.cantidad_movimientos }}</div>
          <!-- Los anulados se dicen, no se esconden: si no, el operador cuenta
               las filas de la tabla y no le da con el total. -->
          <div v-if="totales.cantidad_anulados" class="small text-muted">
            + {{ totales.cantidad_anulados }} anulado(s), fuera del total
          </div>
        </div></div>
      </div>
      <div class="col-md-3">
        <div class="card shadow-sm"><div class="card-body">
          <div class="text-muted small mb-1">Por tipo</div>
          <div v-for="t in totales.por_tipo" :key="t.tipo" class="small">
            {{ t.etiqueta }}: {{ formatoMonto(t.monto) }}
          </div>
          <div v-if="!totales.por_tipo.length" class="small text-muted">—</div>
        </div></div>
      </div>
      <div class="col-md-3">
        <div class="card shadow-sm"><div class="card-body">
          <div class="text-muted small mb-1">Por método</div>
          <div v-for="m in totales.por_metodo_pago" :key="m.metodo_pago" class="small">
            {{ m.metodo_pago }}: {{ formatoMonto(m.total) }}
          </div>
          <div v-if="!totales.por_metodo_pago.length" class="small text-muted">—</div>
        </div></div>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="table-responsive">
        <table class="table align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th>Fecha</th><th>Tipo</th><th>Nombre o Razón Social</th><th>Descripción</th>
              <th>Documento</th><th>Forma</th><th>Cajero</th><th class="text-end">Monto</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="f in filas" :key="f.movimiento_id" :class="{ 'text-muted': f.anulado }">
              <td class="text-nowrap">{{ formatoFecha(f.fecha_movimiento) }}</td>
              <td>
                <span class="badge" :class="claseTipo(f.tipo_ingreso)">{{ f.tipo_etiqueta }}</span>
                <span v-if="f.anulado" class="badge bg-danger-subtle text-danger ms-1">Anulado</span>
              </td>
              <td>{{ f.nombre || '—' }}</td>
              <td>{{ f.descripcion || '—' }}</td>
              <td class="text-nowrap">
                {{ f.documento_tipo === 'RECIBO' ? 'Recibo' : (f.documento_tipo ? 'Factura' : '—') }}
                <span v-if="f.nro_comprobante_origen" class="text-muted small">
                  {{ f.nro_comprobante_origen }}
                </span>
              </td>
              <td>{{ f.metodos_pago || '—' }}</td>
              <td>{{ f.cajero_nombre || '—' }}</td>
              <td class="text-end" :class="{ 'text-decoration-line-through': f.anulado }">
                {{ formatoMonto(f.monto_total) }}
              </td>
            </tr>
            <tr v-if="!filas.length && !loading">
              <td colspan="8" class="text-center text-muted py-4">
                No hubo ingresos en este período con los filtros elegidos.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script setup>
// R6 — Ingresos por período (2026-09-06).
//
// **Por qué existe, además del reporte de ventas.** El de ventas sale de la
// tabla `ventas`, y un cobro de suscripción con recibo no crea venta (RN-40):
// la plata entraba a caja y ningún reporte la mostraba. Reportado por el
// operador tras cobrar en la VPS.
//
// No reemplaza al de ventas —aquel responde lo fiscal: comprobante, IVA
// discriminado— sino que contesta la otra pregunta, que hasta ahora no tenía
// dónde: cuánto entró y de qué. Por eso es UNA pantalla con filtro por tipo y
// no tres reportes separados que después hay que sumar a mano.

import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import reportsService from '@/services/reportsService'
import { fechaIsoLocal } from '@/utils/fechaLocal.mjs'

const route = useRoute()
const hoy = fechaIsoLocal()
const inicioMes = `${hoy.slice(0, 7)}-01`

const TIPOS = [
  { valor: 'COBRO_SUSCRIPCION', texto: 'Cobro de suscripción' },
  { valor: 'VENTA_PRODUCTO', texto: 'Venta de productos' },
  { valor: 'INGRESO_VARIO', texto: 'Ingreso vario' },
  { valor: 'OTRO_COBRO', texto: 'Otro cobro' }
]

const CLASES_TIPO = {
  COBRO_SUSCRIPCION: 'bg-primary-subtle text-primary',
  VENTA_PRODUCTO: 'bg-success-subtle text-success',
  INGRESO_VARIO: 'bg-warning-subtle text-warning-emphasis',
  OTRO_COBRO: 'bg-secondary-subtle text-secondary'
}
const claseTipo = (tipo) => CLASES_TIPO[tipo] || 'bg-secondary-subtle text-secondary'

const filtros = ref({
  fecha_desde: route.query.fecha_desde || inicioMes,
  fecha_hasta: route.query.fecha_hasta || hoy,
  tipo: route.query.tipo || '',
  caja_id: ''
})
const filas = ref([])
const totales = ref(null)
const loading = ref(false)
const error = ref('')

function formatoMonto (valor) {
  return new Intl.NumberFormat('es-PY', { maximumFractionDigits: 0 }).format(Number(valor) || 0)
}
function formatoFecha (valor) {
  return valor ? new Date(valor).toLocaleString('es-PY') : '-'
}

async function cargar () {
  loading.value = true
  error.value = ''
  try {
    const data = await reportsService.getIngresos(filtros.value)
    filas.value = data.filas
    totales.value = data.totales
  } catch (e) {
    error.value = e?.response?.data?.message || 'No se pudo cargar el reporte.'
  } finally {
    loading.value = false
  }
}

async function exportar () {
  try {
    await reportsService.descargarCsv('ingresos', filtros.value, 'ingresos')
  } catch (e) {
    error.value = 'No se pudo exportar el CSV.'
  }
}

onMounted(cargar)
</script>
